import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useCelebration } from '@/contexts/CelebrationContext';

/* Fixed confetti positions so they don't re-randomise on re-render */
const CONFETTI = [
  { color: '#FF6B35', left: 8,  top: 12, rotate: -30, shape: 'rect' },
  { color: '#EC4899', left: 18, top: 6,  rotate:  45, shape: 'circle' },
  { color: '#8B5CF6', left: 30, top: 15, rotate: -15, shape: 'rect' },
  { color: '#F7931E', left: 45, top: 4,  rotate:  30, shape: 'circle' },
  { color: '#10B981', left: 58, top: 10, rotate: -45, shape: 'rect' },
  { color: '#3B82F6', left: 70, top: 5,  rotate:  20, shape: 'circle' },
  { color: '#F59E0B', left: 82, top: 14, rotate: -20, shape: 'rect' },
  { color: '#EF4444', left: 92, top: 8,  rotate:  55, shape: 'circle' },
  { color: '#EC4899', left: 50, top: 20, rotate:  15, shape: 'rect' },
  { color: '#8B5CF6', left: 25, top: 22, rotate: -40, shape: 'circle' },
  { color: '#FF6B35', left: 75, top: 18, rotate:  35, shape: 'rect' },
  { color: '#10B981', left: 12, top: 25, rotate: -25, shape: 'circle' },
];

const CelebrationModal = () => {
  const { current, dismiss } = useCelebration();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!current) return;
    // Auto-dismiss after 7 seconds
    timerRef.current = setTimeout(dismiss, 7000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, dismiss]);

  if (!current) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center px-6"
        onClick={dismiss}
      >
        {/* Confetti layer — above backdrop, below card */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {CONFETTI.map((c, i) => (
            <div
              key={i}
              className="absolute animate-confetti-fall"
              style={{
                left: `${c.left}%`,
                top: `-${c.top}%`,
                width:  c.shape === 'circle' ? 10 : 8,
                height: c.shape === 'circle' ? 10 : 14,
                borderRadius: c.shape === 'circle' ? '50%' : 2,
                background: c.color,
                transform: `rotate(${c.rotate}deg)`,
                animationDelay: `${i * 0.06}s`,
                animationDuration: `${1.2 + (i % 5) * 0.2}s`,
              }}
            />
          ))}
        </div>

        {/* Card */}
        <div
          className="relative w-full max-w-sm bg-white rounded-3xl overflow-hidden animate-celebration-pop"
          style={{ boxShadow: 'var(--shadow-modal)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Gradient header */}
          <div
            className="relative pt-10 pb-8 flex flex-col items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 40%, #EC4899 100%)' }}
          >
            {/* Dismiss button */}
            <button
              onClick={dismiss}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white active:scale-95"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Emoji */}
            <span className="text-7xl leading-none animate-bounce-slow select-none">
              {current.emoji}
            </span>
          </div>

          {/* Content */}
          <div className="px-6 py-6 text-center">
            <h2 className="text-xl font-bold text-foreground mb-2 leading-snug">
              {current.title}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {current.subtitle}
            </p>
            <button
              onClick={dismiss}
              className="mt-6 w-full py-3 rounded-2xl font-semibold text-white active:scale-95"
              style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #EC4899 100%)' }}
            >
              Yay! 🎉
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CelebrationModal;
